/**
 * BackupDriveList — Batch #14.338 : sauvegardes présentes dans le Drive (télécharger / restaurer).
 */
import { useState } from "react";
import { getApiBase, authToken } from "@/api";
import { toast } from "@/lib/toast";
import { Download, History, RefreshCw } from "lucide-react";
import RestoreModal from "./RestoreModal";

const KIND = { daily: ["Quotidienne", "bg-sky-100 text-sky-800"], monthly: ["Mensuelle", "bg-indigo-100 text-indigo-800"], manual: ["Manuelle", "bg-slate-100 text-slate-700"], prerestore: ["Sécurité pré-restauration", "bg-amber-100 text-amber-800"], other: ["Autre", "bg-slate-100 text-slate-600"] };
const mb = (n) => `${(n / 1048576).toFixed(1)} Mo`;
const fmt = (iso) => (iso ? new Date(iso).toLocaleString("fr-CA", { dateStyle: "medium", timeStyle: "short" }) : "—");

export default function BackupDriveList({ drive, onRefresh, onRestored }) {
  const [restoring, setRestoring] = useState(null);
  const [dl, setDl] = useState(null);
  const download = async (it) => {
    setDl(it.file_id);
    try {
      const resp = await fetch(`${getApiBase()}/admin/backup/drive/${it.file_id}/download`, { headers: { Authorization: `Bearer ${authToken()}` } });
      if (!resp.ok) throw new Error((await resp.json().catch(() => ({}))).detail || "Téléchargement impossible");
      const url = URL.createObjectURL(await resp.blob());
      const a = document.createElement("a"); a.href = url; a.download = it.name; document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
    } catch (e) { toast.error(e.message); }
    finally { setDl(null); }
  };
  const items = drive?.items || [];
  return (
    <div className="bg-white border rounded-xl p-5 space-y-3" data-testid="backup-drive-list">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold text-gray-700">Sauvegardes dans le Drive ({items.length})</h3>
        <button onClick={onRefresh} className="text-xs text-slate-500 hover:text-slate-800 flex items-center gap-1" data-testid="backup-drive-refresh"><RefreshCw className="w-3 h-3" /> Actualiser</button>
      </div>
      {drive?.error && <div className="text-xs text-rose-700" data-testid="backup-drive-error">{drive.error}</div>}
      {!drive && <div className="text-xs text-slate-500">Lecture du Drive…</div>}
      {drive && !items.length && !drive.error && <div className="text-xs text-slate-500">Aucune sauvegarde pour l'instant — cliquez « Sauvegarder maintenant ».</div>}
      {items.length > 0 && (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead><tr className="text-left text-xs text-slate-500 border-b"><th className="py-1.5 pr-2">Fichier</th><th className="py-1.5 pr-2">Type</th><th className="py-1.5 pr-2">Créée</th><th className="py-1.5 pr-2 text-right">Taille</th><th className="py-1.5 text-right">Actions</th></tr></thead>
            <tbody>
              {items.map((it) => {
                const [label, cls] = KIND[it.kind] || KIND.other;
                return (
                  <tr key={it.file_id} className="border-b last:border-0" data-testid={`backup-drive-row-${it.file_id}`}>
                    <td className="py-1.5 pr-2 font-mono text-xs text-slate-700">{it.name}</td>
                    <td className="py-1.5 pr-2"><span className={`inline-block px-1.5 rounded text-[11px] ${cls}`}>{label}</span></td>
                    <td className="py-1.5 pr-2 text-xs text-slate-600">{fmt(it.created_at)}</td>
                    <td className="py-1.5 pr-2 text-right text-xs tabular-nums">{mb(it.size)}</td>
                    <td className="py-1.5 text-right whitespace-nowrap">
                      <button onClick={() => download(it)} disabled={dl === it.file_id} className="text-slate-500 hover:text-indigo-700 p-1" title="Télécharger l'archive chiffrée" data-testid={`backup-download-${it.file_id}`}><Download className="w-4 h-4" /></button>
                      <button onClick={() => setRestoring(it)} className="text-slate-500 hover:text-rose-700 p-1" title="Restaurer cette sauvegarde (remplace les données actuelles)" data-testid={`backup-restore-${it.file_id}`}><History className="w-4 h-4" /></button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
      {restoring && <RestoreModal item={restoring} onClose={() => setRestoring(null)} onDone={() => { setRestoring(null); onRestored(); }} />}
    </div>
  );
}
